// Default classes per part; every part also carries `data-part` / `data-state`.

import { cva, type VariantProps } from "class-variance-authority";
import type { DateTimePickerClassNames, DayState } from "./types";

export const triggerVariants = cva(
  [
    "flex w-full items-center gap-2 rounded-md border bg-input-background",
    "text-left transition-colors outline-none",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-8 px-2.5 text-sm",
        md: "h-10 px-3 text-sm",
        lg: "h-12 px-4 text-base",
      },
      state: {
        default: "border-border text-foreground",
        error: "border-destructive text-foreground focus-visible:ring-destructive",
        success: "border-emerald-600 text-foreground focus-visible:ring-emerald-600",
      },
    },
    defaultVariants: { size: "md", state: "default" },
  },
);

export type TriggerVariantProps = VariantProps<typeof triggerVariants>;

/** Base classes per part, merged with the caller's `classNames` via `cn()`. */
export const PART_CLASSES: Required<
  Pick<
    DateTimePickerClassNames,
    | "popover"
    | "presets"
    | "presetButton"
    | "calendar"
    | "months"
    | "month"
    | "caption"
    | "captionLabel"
    | "nav"
    | "navButton"
    | "weekdays"
    | "weekday"
    | "week"
    | "day"
    | "dayButton"
    | "daySelected"
    | "dayToday"
    | "dayOutside"
    | "dayDisabled"
    | "dayPreview"
    | "rangeStart"
    | "rangeMiddle"
    | "rangeEnd"
    | "timePanel"
    | "timeColumn"
    | "timeOption"
    | "footer"
  >
> = {
  popover: [
    "z-[60] w-auto min-w-[300px] max-w-[calc(100vw-16px)] rounded-xl border border-border bg-popover p-4 shadow-xl",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  ].join(" "),

  presets: "mb-3 flex flex-wrap gap-2 border-b border-border pb-3",
  presetButton: [
    "cursor-pointer rounded-lg border border-border bg-card px-2.5 py-1 text-xs font-medium",
    "text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  ].join(" "),

  calendar: "text-sm text-foreground",
  // Panes stack on narrow screens, so a two-month picker needs no side scroll.
  months: "flex flex-col gap-4 sm:flex-row sm:gap-6",
  month: "relative flex flex-col",

  caption: "flex h-10 items-center justify-between gap-1 mb-2 px-0.5",
  captionLabel: "text-sm font-semibold text-foreground text-center flex-1",
  nav: "flex items-center gap-0.5",
  navButton: [
    "inline-flex size-7 cursor-pointer items-center justify-center rounded-md",
    "border border-border/70 bg-card text-muted-foreground transition-colors",
    "hover:bg-accent/60 hover:text-foreground hover:border-border",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-30",
  ].join(" "),

  weekdays: "grid grid-cols-7 pb-1",
  weekday:
    "flex h-9 items-center justify-center text-center text-xs font-medium text-muted-foreground",
  week: "grid grid-cols-7",

  day: "relative p-0 text-center",
  // 40px targets keep the grid touch-friendly on mobile.
  dayButton: [
    "mx-auto inline-flex size-10 cursor-pointer items-center justify-center rounded-lg text-sm",
    "text-foreground transition-colors hover:bg-accent/60",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  ].join(" "),
  daySelected:
    "bg-primary font-medium text-primary-foreground hover:bg-primary focus:bg-primary",
  dayToday: "font-semibold text-primary",
  dayOutside: "text-muted-foreground opacity-40",
  dayDisabled:
    "cursor-not-allowed text-muted-foreground opacity-30 hover:bg-transparent",
  dayPreview: "bg-primary/10",

  rangeStart: "rounded-r-none",
  rangeMiddle: "rounded-none bg-primary/10 text-foreground hover:bg-primary/20",
  rangeEnd: "rounded-l-none",

  timePanel: "flex items-stretch justify-center gap-2",
  timeColumn:
    "h-[216px] w-20 overflow-y-auto rounded-lg border border-border bg-card py-1 scroll-smooth",
  timeOption: [
    "flex h-9 w-full cursor-pointer items-center justify-center text-sm text-foreground",
    "transition-colors hover:bg-accent/60",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "aria-selected:bg-primary aria-selected:font-medium aria-selected:text-primary-foreground",
  ].join(" "),

  footer: "mt-3 border-t border-border pt-3",
};

/** Serialize day state for `data-state`, so CSS can target it with no props. */
export function stateAttr(state: DayState): string {
  const flags: string[] = [];
  if (state.selected) flags.push("selected");
  if (state.today) flags.push("today");
  if (state.outside) flags.push("outside");
  if (state.disabled) flags.push("disabled");
  if (state.rangeStart) flags.push("range-start");
  if (state.rangeMiddle) flags.push("range-middle");
  if (state.rangeEnd) flags.push("range-end");
  if (state.preview) flags.push("preview");
  if (state.focused) flags.push("focused");
  return flags.join(" ");
}
